import { environment } from '../../../environments/environment';

const baseURL = environment.API_ENDPOINT;

const v1Base = `${baseURL}/v1`;
const v2Base = `${baseURL}/v2`;

const serializeQueryParams = (obj, prefix = null) => {
  const str = [];
  for (let [k, v] of Object.entries(obj || {})) {
    if (v === undefined) {
      continue;
    }
    k = prefix ? `${prefix}[${k}]` : k;

    const value = typeof v === 'object' ? serializeQueryParams(v, k) : `${k}=${v}`;
    str.push(value);
  }
  return str.join('&');
};

export const v1URL = {
  account: {
    checkEmail: {
      method: 'get',
      url: params => `${v1Base}/account/check?email=${encodeURIComponent(params?.email)}`,
    },
    checkPhone: {
      method: 'get',
      url: params => `${v1Base}/account/check?phone=${encodeURIComponent(params?.phone)}`,
    },
  },
  appData: {
    method: 'get',
    url: `${v1Base}/appdata`,
  },
  project: {
    method: 'get',
    url: params => `${v1Base}/project?${serializeQueryParams(params?.qp)}`,
    update: {
      method: 'post',
      url: () => `/v1/project`,
    },
    // Daily reports tab: map data (shapes / pins) of the filtered reports.
    // Host-less: consumed through AppHttpService.
    reportMapData: {
      available: {
        method: 'get',
        url: params => `/v1/project/${params?.projectId}/report-map-data`,
      },
      // kmz | geojson file, named by Content-Disposition ("<project> - Map data.<ext>").
      export: {
        method: 'post',
        url: params => `/v1/project/${params?.projectId}/report-map-data/${params?.format}`,
      },
    },
  },
  issue: {
    method: 'get',
    url: params => `${v1Base}/issue/summary/${params?.entityType}/${params?.project.id}`,

    fetch: {
      method: 'get',
      url: params => `${v1Base}/issue/summary/${params?.entityType}/${params?.project.id}`,
    },

    update: {
      method: 'put',
      url: params => `${v1Base}/issue/${params?.id}?${serializeQueryParams(params?.qp)}`,
    },
    add: {
      method: 'post',
      url: () => `${v1Base}/issue`,
    },
    changeStatus: {
      method: 'post',
      url: params => `${v1Base}/issue/${params?.id}/${params?.status}?${serializeQueryParams(params?.qp)}`,
    },
    // Contractor workflow (projects with issue_workflow_enabled). No `include`:
    // the API's default include carries contractor_accounts and sub_accounts.
    get: {
      method: 'get',
      url: params => `${v1Base}/issue/${params?.issueId}`,
    },
    contractorUsers: {
      method: 'get',
      url: params => `${v1Base}/issue/${params?.issueId}/contractor-users`,
    },
    // Project Issues tab. Host-less: consumed through AppHttpService, with the
    // tab's filters as the query (issuesTabApiQuery).
    // pdf | csv | kmz file, named by Content-Disposition ("<project> - Issues.<ext>").
    export: {
      method: 'get',
      url: params => `/v1/issue/export/${params?.format}`,
    },
    // Every matching issue with coordinates, unpaginated, for the project map.
    forMap: {
      method: 'get',
      url: () => `/v1/issue/for-map`,
    },
  },
  dailyReports: {
    fetchByProject: {
      method: 'get',
      url: params => `${v1Base}/daily-report/project/${params?.project.id}?${serializeQueryParams(params?.qp)}`,
    },
  },
  dailyReport: {
    fetch: {
      method: 'get',
      url: params => `${v1Base}/daily-report/${params?.reportId}?${serializeQueryParams(params?.qp)}`,
    },
    create: {
      method: 'post',
      url: params => `${v1Base}/daily-report/${params?.siteId}/draft`,
    },
    delete: {
      method: 'delete',
      url: params => `${v1Base}/daily-report/${params?.reportId}`,
    },
    addContractorStatus: {
      method: 'patch',
      url: params =>
        `${v1Base}/daily-report/${params?.dailyReportId}/report-bid-item/${params?.reportBidItemId}/contractor-status`,
    },

    // daily-report/7bBWjS91CZzdYnCCX6wSR9/report-bid-item/o13dFZtH6LTGFMYazkCjbB/contractor-status
  },
  dailyDiary: {
    list: {
      method: 'get',
      url: params => {
        const qp = new URLSearchParams();
        if (params?.startDate) qp.set('startDate', params.startDate);
        if (params?.endDate) qp.set('endDate', params.endDate);
        const query = qp.toString();
        return `${v1Base}/daily-diary/${params?.siteId}${query ? `?${query}` : ''}`;
      },
    },
    get: {
      method: 'get',
      // NOTE the /diary segment: `/daily-diary/:id` is the LIST-BY-SITE route,
      // so calling it with a diary id 404s ("Record not found site.<id>") and
      // the form silently rendered an empty diary with no remarks.
      url: params => `${v1Base}/daily-diary/diary/${params?.diaryId}`,
    },
    saveDraft: {
      method: 'post',
      url: params => `${v1Base}/daily-diary/${params?.siteId}/draft`,
    },
    // The same endpoint the mobile app reads before writing a diary: the api
    // stores whatever weather it is handed, so the client seeds it.
    weather: {
      method: 'get',
      url: params => `${v1Base}/weather?siteId=${params?.siteId}&date=${params?.date}`,
    },
    submit: {
      method: 'patch',
      url: params => `${v1Base}/daily-diary/diary/${params?.diaryId}/submit`,
    },
    delete: {
      method: 'delete',
      url: params => `${v1Base}/daily-diary/diary/${params?.diaryId}`,
    },
    addRemark: {
      method: 'post',
      url: params => `${v1Base}/daily-diary/diary/${params?.diaryId}/remark`,
    },
    deleteRemark: {
      method: 'delete',
      url: params => `${v1Base}/daily-diary/diary/${params?.diaryId}/remark/${params?.remarkId}`,
    },
    remarkCategories: {
      method: 'get',
      url: () => `${v1Base}/daily-diary/remark-categories`,
    },
  },
  getProjectDetailsById: {
    method: 'get',
    url: params => `${v1Base}/project/${params?.projectId}?${serializeQueryParams(params?.qp)}`,
  },
  issues: {
    method: 'get',
    url: params => `${v1Base}/issue/?${serializeQueryParams(params?.qp)}`,
  },
  projectReports: {
    fetch: {
      method: 'get',
      url: params => `${v1Base}/project-report?${serializeQueryParams(params?.qp)}`,
    },
  },
  projectMembers: {
    fetch: {
      method: 'get',
      url: params => `${v1Base}/project/${params?.projectId}/accounts`,
    },
    members: {
      method: 'get',
      url: params => `${v1Base}/project/${params?.projectId}/members`,
    },
    availableMembers: {
      method: 'get',
      url: params => `${v1Base}/project/${params?.projectId}/available-members`,
    },
    invite: {
      method: 'post',
      url: params => `${v1Base}/project/${params?.projectId}/member`,
    },
    update: {
      method: 'put',
      url: params => `${v1Base}/project/${params?.projectId}/member/${params?.accountId}`,
    },
    delete: {
      method: 'delete',
      url: params => `${v1Base}/project/${params?.projectId}/member/${params?.accountId}`,
    },
  },
  projectRoles: {
    fetch: {
      method: 'get',
      url: params => `${v1Base}/project/${params?.projectId}/roles`,
    },
  },
  projectAccounts: {
    fetch: {
      method: 'get',
      url: params => `${v1Base}/project/${params?.projectId}/accounts?${serializeQueryParams(params?.qp)}`,
    },
  },
  projectInvitations: {
    fetch: {
      method: 'get',
      url: params => `${v1Base}/invitation?${serializeQueryParams({ project_id: params?.projectId, ...params?.qp })}`,
    },
    createForProject: {
      method: 'post',
      url: params =>
        `${v1Base}/invitation/project/${params?.projectId}/${params?.role}?${serializeQueryParams(params?.qp)}`,
    },
    createForSite: {
      method: 'post',
      url: params => `${v1Base}/invitation/site/${params?.siteId}/${params?.role}?${serializeQueryParams(params?.qp)}`,
    },
  },
  projectSites: {
    fetch: {
      method: 'get',
      url: params =>
        `${v1Base}/project/${params?.projectId}/sites${params?.qp ? `?${serializeQueryParams(params.qp)}` : ''}`,
    },
  },
  site: {
    create: {
      method: 'post',
      url: () => `${v1Base}/site`,
    },
    update: {
      method: 'put',
      url: params => `${v1Base}/site/${params?.id || params?.siteId || params?.uuid}`,
    },
    delete: {
      method: 'delete',
      url: params => `${v1Base}/site/${params?.id || params?.siteId || params?.uuid}`,
    },
  },
  siteDetails: {
    fetch: {
      method: 'get',
      url: params => `${v1Base}/site/${params?.siteId}?${serializeQueryParams(params?.qp)}`,
    },
  },
  attachment: {
    method: 'post',
    url: `${v1Base}/attachment`,
  },
  bidSheet: {
    fetch: {
      method: 'get',
      url: params => `${v1Base}/bid/${params?.projectId}/sheet?`,
    },
    upload: {
      method: 'post',
      url: params => `${v1Base}/bid/sheet/${params?.bidSheetId}/csv`,
    },
    fetchTemplateFile: {
      method: 'get',
      url: params => `${v1Base}/bid/sheet/${params?.bidSheetId}/csv-template`,
    },
    downloadCsv: {
      method: 'get',
      // Host-less on purpose: consumed via AppHttpService, which prepends the API endpoint.
      url: params => `/v1/bid/sheet/${params?.bidSheetId}/csv`,
    },
  },
  bidItemsList: {
    fetch: {
      method: 'get',
      url: params => `${v1Base}/bid/sheet/${params?.bidSheetId}/item?${serializeQueryParams(params?.qp)}`,
    },
  },
  bidItem: {
    fetch: {
      method: 'get',
      url: params => `${v1Base}/bid/item/${params?.bidItemId}?${serializeQueryParams(params?.qp || {})}`,
    },
    create: {
      method: 'post',
      url: params => `${v1Base}/bid/sheet/${params?.bidSheetId}/item`,
    },
    update: {
      method: 'put',
      url: params => `${v1Base}/bid/item/${params?.bidItemId}`,
    },
    delete: {
      method: 'delete',
      url: params => `${v1Base}/bid/item/${params?.bidItemId}`,
    },
  },
  bidItemSitesList: {
    fetch: {
      method: 'get',
      url: params => `${v1Base}/bid/item/${params?.bidItemId}?${serializeQueryParams(params?.qp)}`,
    },
  },
  changeOrders: {
    fetch: {
      method: 'get',
      url: params => `${v1Base}/bid/${params?.projectId}/change-order`,
    },
    fetchBidItemList: {
      method: 'get',
      url: params =>
        `${v1Base}/bid/${params?.projectId}/change-order/${params?.changeOrderId}?${serializeQueryParams(params?.qp)}`,
    },
    fetchChangeOrderFiltersInContextOfBidItems: {
      method: 'get',
      url: params => `${v1Base}/bid/item/${params?.bidItemId}?${serializeQueryParams(params?.qp)}`,
    },
  },
  payapp: {
    fetchPayappItems: {
      method: 'get',
      url: params => `${v2Base}/project/${params?.projectId}/pay-app?${serializeQueryParams(params?.qp)}`,
    },
    create: {
      method: 'post',
      url: params => `${v2Base}/project/${params?.projectId}/pay-app${params?.dryRun ? '?dryRun=true' : ''}`,
    },
    fetchMohBalances: {
      method: 'get',
      url: params => `${v2Base}/project/${params?.projectId}/moh-balances`,
    },
    uploadMohAttachment: {
      method: 'post',
      url: params => `${v2Base}/project/${params?.projectId}/pay-app/${params?.payappId}/moh/${params?.mohRowId}/attachments`,
    },
    deleteMohAttachment: {
      method: 'delete',
      url: params => `${v2Base}/project/${params?.projectId}/pay-app/${params?.payappId}/moh/${params?.mohRowId}/attachments/${params?.attachmentId}`,
    },
    fetchPayapps: {
      method: 'get',
      url: params => {
        const includes = (params?.qp?.include || []).map(i => `include[]=${i}`).join('&');
        return `${v2Base}/project/${params?.projectId}/pay-apps${includes ? '?' + includes : ''}`;
      },
    },
    fetchPayappDetails: {
      method: 'get',
      url: params => {
        const includes = (params?.qp?.include || []).map(i => `include[]=${i}`).join('&');
        return `${v2Base}/project/${params?.projectId}/pay-app/${params?.payappId}${includes ? '?' + includes : ''}`;
      },
    },
    updatePayappStatus: {
      method: 'patch',
      url: params =>
        `${v1Base}/daily-report/${params?.dailyReportId}/report-bid-item/${params?.reportBidItemId}/pay-app-status`,
    },
    delete: {
      method: 'delete',
      url: params => `${v2Base}/project/${params?.projectId}/pay-app/${params?.payappId}`,
    },
    download: {
      method: 'get',
      url: params => `/v2/project/${params?.projectId}/pay-app/${params?.payappId}/download`,
    },
    mohLedgerDownload: {
      method: 'get',
      url: params => `/v2/project/${params?.projectId}/moh-ledger/download`,
    },
  },
  subContractor: {
    fetch: {
      method: 'get',
      url: params => `${v1Base}/sub-contractor/?project_id=${params?.projectId}&${serializeQueryParams(params?.qp)}`,
    },
    fetchByProject: {
      method: 'get',
      url: params => `${v1Base}/project/${params?.projectId}/sub-contractor`,
    },
    create: {
      method: 'post',
      url: params => `${v1Base}/project/${params?.projectId}/sub-contractor`,
    },
    update: {
      method: 'put',
      url: params =>
        `${v1Base}/project/${params?.projectId}/sub-contractor/${params?.contractorId}`,
    },
    delete: {
      method: 'delete',
      url: params =>
        `${v1Base}/project/${params?.projectId}/sub-contractor/${params?.contractorId}`,
    },
  },
  labor: {
    fetch: {
      method: 'get',
      url: params => `${v1Base}/lookup/labor?entity_id=${params?.projectId}&entity_type=project`,
    },
  },
  equipment: {
    fetch: {
      method: 'get',
      url: params => `${v1Base}/lookup/equipment?entity_id=${params?.projectId}&entity_type=project`,
    },
  },
  workflow: {
    state: {
      execute: {
        method: 'post',
        url: params => `${v1Base}/workflow/execute?${serializeQueryParams(params?.qp)}`,
      },
    },
    execute: {
      method: 'post',
      url: params => `${v1Base}/workflow/execute?${serializeQueryParams(params?.qp)}`,
    },
    submit: {
      method: 'post',
      url: params =>
        `${v1Base}/workflow/execute?wf=submittal-approval&ex=submittal-approval/${params.id}&state=submittal-submit`,
    },
    nextState: {
      method: 'get',
      url: params => `${v1Base}/workflow/next-state?${serializeQueryParams(params?.qp)}`,
    },
    lineage: {
      method: 'get',
      url: params => `${v1Base}/workflow/lineage?${serializeQueryParams(params?.qp)}`,
    },
    rfi: {
      submit: {
        method: 'post',
        url: params => `${v1Base}/workflow/execute?wf=rfi-approval&ex=rfi-approval/${params.id}&state=rfi-submit`,
      },
    },
  },
  siteMaterialTest: {
    method: 'get',
    url: params => `${v1Base}/site-material-test/site/${params?.siteId}?${serializeQueryParams(params?.qp)}`,
  },
  search: {
    method: 'get',
    // encodeURIComponent: raw '%', '&', '#' etc. in the query break the URL
    // ("Densities on 5%" produced a malformed percent-escape and the request
    // failed outright)
    url: params => `${v1Base}/search?q=${encodeURIComponent(params?.searchText ?? '')}`,
  },
  activityCode: {
    fetch: {
      method: 'get',
      url: params => `${v1Base}/activity-code/${params?.projectId}?${serializeQueryParams(params?.qp)}`,
    },
    upload: {
      method: 'post',
      url: params => `${v1Base}/activity-code/${params?.projectId}/csv`,
    },
    update: {
      method: 'post',
      url: params => `${v1Base}/activity-code/${params?.projectId}`,
    },
    delete: {
      method: 'delete',
      url: params => `${v1Base}/activity-code/${params?.id}`,
    },
  },
  lookUp: {
    basisOfPay: {
      fetch: {
        method: 'get',
        url: params => `${v1Base}/lookup/basis_of_pay?entity_type=${params?.entityType}&entity_id=${params?.entityId}`,
      },
    },
    changeInWorkCode: {
      fetch: {
        method: 'get',
        url: params =>
          `${v1Base}/lookup/change_order_code?entity_type=${params?.entityType}&entity_id=${params?.entityId}`,
      },
    },
  },
  changeInWorkRequest: {
    fetchWithId: {
      method: 'get',
      url: params => `${v1Base}/change-order-request/${params?.changeInWorkRequestId}`,
    },
    fetch: {
      method: 'get',
      url: params => `${v1Base}/change-order-request/project/${params?.projectId}?${serializeQueryParams(params?.qp)}`,
    },
    save: {
      method: 'post',
      url: () => `${v1Base}/change-order-request`,
    },
    delete: {
      method: 'delete',
      url: params => `${v1Base}/change-order-request/${params.id}`,
    },
    sendReminder: {
      method: 'post',
      url: params => `${v1Base}/change-order-request/${params?.id}/reminder`,
    },
    fetchAvailableCors: {
      method: 'get',
      url: params => `${v1Base}/change-order-request/project/${params?.projectId}?available=true&start=0&total=1000`,
    },
    fetchPendingBidItems: {
      method: 'get',
      url: params => `${v1Base}/change-order-request/project/${params?.projectId}/pending-bid-items${params?.exclude ? `?exclude=${params.exclude}` : ''}`,
    },
  },
  changeOrdersRequest: {
    fetchWithId: {
      method: 'get',
      url: params => `${v1Base}/change-order/${params?.changeOrdersRequestId}`,
    },
    fetch: {
      method: 'get',
      url: params => `${v1Base}/change-order/project/${params?.projectId}?${serializeQueryParams(params?.qp)}`,
    },
    save: {
      method: 'post',
      url: () => `${v1Base}/change-order`,
    },
    sendReminder: {
      method: 'post',
      url: params => `${v1Base}/change-order-request/${params?.id}/reminder`,
    },
    createFromRequests: {
      method: 'post',
      url: () => `${v1Base}/change-order/create-from-requests`,
    },
    linkRequests: {
      method: 'post',
      url: params => `${v1Base}/change-order/${params?.changeOrderId}/link-requests`,
    },
    linkedRequests: {
      method: 'get',
      url: params => `${v1Base}/change-order/${params?.changeOrderId}/linked-requests`,
    },
    unlinkRequest: {
      method: 'delete',
      url: params => `${v1Base}/change-order/${params?.changeOrderId}/link-requests/${params?.corId}`,
    },
    delete: {
      method: 'delete',
      url: params => `${v1Base}/change-order/${params?.id}`,
    },
  },
  submittals: {
    log: {
      method: 'post',
      url: params => `${v1Base}/submittals/log?projectId=${params?.projectId}`,
    },
    registry: {
      method: 'get',
      url: params => `${v1Base}/submittals/log?${serializeQueryParams(params)}`,
    },
    item: {
      method: 'get',
      url: params => `${v1Base}/submittals/item?${serializeQueryParams(params)}`,
    },
    fetchWithId: {
      method: 'get',
      url: params => `${v1Base}/submittals/item/${params?.submittalsItemId}`,
    },
    create: {
      item: {
        method: 'post',
        url: params => `${v1Base}/submittals/item?projectId=${params?.projectId}&${serializeQueryParams(params?.qp)}`,
      },
    },
    submitReviewers: {
      method: 'post',
      url: params => `${v1Base}/submittals/item/${params?.id}/reviewers`,
    },
    upload: {
      method: 'post',
      url: params => `${v1Base}/submittals/log/csv?projectId=${params?.projectId}`,
    },
    delete: {
      method: 'delete',
      url: params => `${v1Base}/submittals/item/${params?.id}`,
    },
  },
  users: {
    list: {
      method: 'get',
      url: params => `${v1Base}/project/${params?.projectId}/accounts?${serializeQueryParams(params?.qp)}`,
    },
  },
  rfis: {
    delete: {
      method: 'delete',
      url: params => `${v1Base}/rfi/${params?.id}`,
    },
    fetchNo: {
      method: 'get',
      url: params => `${v1Base}/rfi/next-number/${params?.projectId}`,
    },
    create: {
      method: 'post',
      url: params => `${v1Base}/rfi?projectId=${params?.projectId}`,
    },
    list: {
      method: 'get',
      url: params => `${v1Base}/rfi?${serializeQueryParams(params?.qp)}`,
    },
    fetchWithId: {
      method: 'get',
      url: params => `${v1Base}/rfi/${params?.rifId}`,
    },
    submitReviewers: {
      method: 'post',
      url: params => `${v1Base}/rfi/${params?.id}/reviewers`,
    },
  },
  sampleRecord: {
    list: {
      method: 'get',
      url: params => `${v1Base}/sample-record?${serializeQueryParams(params?.qp)}`,
    },
    fetchByUuid: {
      method: 'get',
      url: params => `${v1Base}/sample-record/${params?.uuid}`,
    },
    create: {
      method: 'post',
      url: () => `${v1Base}/sample-record`,
    },
    update: {
      method: 'patch',
      url: params => `${v1Base}/sample-record/${params?.uuid}`,
    },
    delete: {
      method: 'delete',
      url: params => `${v1Base}/sample-record/${params?.uuid}`,
    },
    push: {
      method: 'post',
      url: params => `${v1Base}/sample-record/${params?.uuid}/push`,
    },
    materials: {
      method: 'get',
      url: params => `${v1Base}/sample-record/materials?${serializeQueryParams(params?.qp)}`,
    },
    sources: {
      method: 'get',
      url: params => `${v1Base}/sample-record/sources?${serializeQueryParams(params?.qp)}`,
    },
    testMethods: {
      method: 'get',
      url: params => `${v1Base}/sample-record/test-methods?${serializeQueryParams(params?.qp)}`,
    },
  },
  mgvwRecord: {
    // Contract-level MGVW (Maximum Gross Vehicle Weight) agency view — the
    // api's /v1/mgvw-record router. The list is per project, unpaginated,
    // newest first; pdf/xls are binary downloads fetched via AppHttpService.
    list: {
      method: 'get',
      url: params => `${v1Base}/mgvw-record/project/${params?.projectId}`,
    },
    create: {
      method: 'post',
      url: () => `${v1Base}/mgvw-record`,
    },
    update: {
      method: 'put',
      url: params => `${v1Base}/mgvw-record/${params?.uuid}`,
    },
    delete: {
      method: 'delete',
      url: params => `${v1Base}/mgvw-record/${params?.uuid}`,
    },
    submit: {
      method: 'post',
      url: params => `${v1Base}/mgvw-record/${params?.uuid}/submit`,
    },
    pdf: {
      method: 'get',
      path: params => `/v1/mgvw-record/${params?.uuid}/pdf`,
    },
    xls: {
      method: 'get',
      path: params => `/v1/mgvw-record/project/${params?.projectId}/xls`,
    },
  },
  risk: {
    // Risk register — the api's /v1/risk router. Host-less paths: consumed through
    // AppHttpService (which prepends the API endpoint and serialises arrays as
    // status[]=a&status[]=b, which the api's Joi list query accepts).
    list: params => `/v1/risk/project/${params?.projectId}/risks`,
    heatMap: params => `/v1/risk/project/${params?.projectId}/risks/heatmap`,
    config: params => `/v1/risk/project/${params?.projectId}/risks/config`,
    create: params => `/v1/risk/project/${params?.projectId}/risks`,
    propose: params => `/v1/risk/project/${params?.projectId}/risks/propose`,
    import: params => `/v1/risk/project/${params?.projectId}/risks/import`,
    export: params => `/v1/risk/project/${params?.projectId}/risks/export`,
    one: params => `/v1/risk/risks/${params?.riskId}`,
    updates: params => `/v1/risk/risks/${params?.riskId}/updates`,
    action: params => `/v1/risk/risks/${params?.riskId}/${params?.action}`, // approve | reject | close | reopen | convert-to-issue
  },
  materialTestLab: {
    fetchList: {
      method: 'get',
      url: params => `${v1Base}/material-test-lab?start=0&total=1000&projectId=${params?.projectId || ''}`,
    },
    create: {
      method: 'post',
      url: params => `${v1Base}/material-test-lab/${params?.projectId}`,
    },
    update: {
      method: 'put',
      url: params => `${v1Base}/material-test-lab/${params?.body?.id}`,
    },
  },
  externalMaterialTest: {
    fetchTestNameList: {
      method: 'get',
      url: params => `${v1Base}/project/${params?.projectId}/material-test?${serializeQueryParams(params?.qp)}`,
    },
    create: {
      method: 'post',
      url: params => `${v1Base}/project/${params?.projectId}/material-test`,
    },
    update: {
      method: 'put',
      url: params => `${v1Base}/project/${params?.projectId}/material-test/${params?.testId}`,
    },
    delete: {
      method: 'delete',
      url: params => `${v1Base}/project/${params?.projectId}/material-test/${params?.testId}`,
    },
    fetchLabList: {
      method: 'get',
      url: params => `${v1Base}/material-test-lab?${serializeQueryParams(params?.qp)}`,
    },
    save: {
      method: 'post',
      url: params => `${v1Base}/site-material-test/${params?.siteId}/draft`,
    },
    sendForLabTest: {
      method: 'post',
      url: params => `${v1Base}/site-material-test/${params?.testId}/send-to-lab`,
    },
    acceptLabSchedule: {
      method: 'post',
      url: params => `${v1Base}/site-material-test/${params?.testId}/accept-schedule`,
    },
    record: {
      method: 'post',
      url: params => `${v1Base}/site-material-test/${params?.testId}/record`,
    },
    updateReport: {
      method: 'post',
      url: params => `${v1Base}/site-material-test/${params?.testId}/report`,
    },
    updateResult: {
      method: 'post',
      url: params => `${v1Base}/site-material-test/${params?.testId}/${params?.result}`,
    },
    cancel: {
      method: 'post',
      url: params => `${v1Base}/site-material-test/${params?.testId}/cancel`,
    },
  },
  documents: {
    folder: {
      delete: {
        method: 'delete',
        url: params => `${v1Base}/document-folder/${params?.folderId}`,
      },
      fetchAll: {
        method: 'get',
        url: params => `${v1Base}/document-folder/root?projectId=${params?.projectId}&nested=true`,
      },
      createFolder: {
        method: 'post',
        url: params => `${v1Base}/document-folder?projectId=${params?.projectId}`,
      },
      updateName: {
        method: 'put',
        url: params => `${v1Base}/document-folder/${params?.folderId}`,
      },
    },
    file: {
      add: {
        method: 'post',
        url: params => `${v1Base}/document?projectId=${params?.projectId}`,
      },
      delete: {
        method: 'delete',
        url: params => `${v1Base}/document/${params?.documentId}`,
      },
      updateName: {
        method: 'put',
        url: params => `${v1Base}/document/${params?.documentId}`,
      },
    },
  },
  concreteBatchTicket: {
    extract: {
      method: 'post',
      url: () => `${v1Base}/concrete-batch-ticket/extract`,
    },
  },
  warranty: {
    list: {
      method: 'get',
      url: params => `${v1Base}/warranty/project/${params?.projectId}/warranties`,
    },
    create: {
      method: 'post',
      url: params => `${v1Base}/warranty/project/${params?.projectId}/warranties`,
    },
    get: {
      method: 'get',
      url: params => `${v1Base}/warranty/project/${params?.projectId}/warranties/${params?.warrantyId}`,
    },
    update: {
      method: 'patch',
      url: params => `${v1Base}/warranty/project/${params?.projectId}/warranties/${params?.warrantyId}`,
    },
    delete: {
      method: 'delete',
      url: params => `${v1Base}/warranty/project/${params?.projectId}/warranties/${params?.warrantyId}`,
    },
    claims: {
      list: {
        method: 'get',
        url: params => {
          const qp = new URLSearchParams();
          qp.set('start', String(params?.start ?? 0));
          qp.set('total', String(params?.total ?? 10));
          if (params?.search) qp.set('search', params.search);
          if (Array.isArray(params?.status) && params.status.length) {
            params.status.forEach(s => qp.append('status', s));
          } else if (params?.status) {
            qp.set('status', params.status);
          }
          if (Array.isArray(params?.created_by) && params.created_by.length) {
            params.created_by.forEach(c => qp.append('created_by', c));
          } else if (params?.created_by) {
            qp.set('created_by', params.created_by);
          }
          if (params?.sort_by) qp.set('sort_by', params.sort_by);
          if (params?.sort_order) qp.set('sort_order', params.sort_order);
          return `${v1Base}/warranty/project/${params?.projectId}/warranties/${params?.warrantyId}/claims?${qp.toString()}`;
        },
      },
      create: {
        method: 'post',
        url: params => `${v1Base}/warranty/project/${params?.projectId}/warranties/${params?.warrantyId}/claims`,
      },
      get: {
        method: 'get',
        url: params => `${v1Base}/warranty/claims/${params?.claimId}`,
      },
      update: {
        method: 'patch',
        url: params => `${v1Base}/warranty/claims/${params?.claimId}`,
      },
      submit: {
        method: 'post',
        url: params => `${v1Base}/warranty/claims/${params?.claimId}/submit`,
      },
      approve: {
        method: 'post',
        url: params => `${v1Base}/warranty/claims/${params?.claimId}/approve`,
      },
      reject: {
        method: 'post',
        url: params => `${v1Base}/warranty/claims/${params?.claimId}/reject`,
      },
      respond: {
        method: 'post',
        url: params => `${v1Base}/warranty/claims/${params?.claimId}/respond`,
      },
      dispute: {
        method: 'post',
        url: params => `${v1Base}/warranty/claims/${params?.claimId}/dispute`,
      },
      complete: {
        method: 'post',
        url: params => `${v1Base}/warranty/claims/${params?.claimId}/complete`,
      },
      verify: {
        method: 'post',
        url: params => `${v1Base}/warranty/claims/${params?.claimId}/verify`,
      },
      'need-info': {
        method: 'post',
        url: params => `${v1Base}/warranty/claims/${params?.claimId}/need-info`,
      },
      escalate: {
        method: 'post',
        url: params => `${v1Base}/warranty/claims/${params?.claimId}/escalate`,
      },
      activities: {
        method: 'get',
        url: params => `${v1Base}/warranty/claims/${params?.claimId}/activities`,
      },
      addActivity: {
        method: 'post',
        url: params => `${v1Base}/warranty/claims/${params?.claimId}/activities`,
      },
    },
  },
  punchlist: {
    get: {
      method: 'get',
      url: params => `${v1Base}/punchlist/project/${params?.projectId}/punchlist`,
    },
    create: {
      method: 'post',
      url: params => `${v1Base}/punchlist/project/${params?.projectId}/punchlist`,
    },
    update: {
      method: 'patch',
      url: params => `${v1Base}/punchlist/project/${params?.projectId}/punchlist/${params?.punchlistId}`,
    },
    delete: {
      method: 'delete',
      url: params => `${v1Base}/punchlist/project/${params?.projectId}/punchlist/${params?.punchlistId}`,
    },
    participants: {
      list: {
        method: 'get',
        url: params => `${v1Base}/punchlist/project/${params?.projectId}/punchlist/${params?.punchlistId}/participants`,
      },
      add: {
        method: 'post',
        url: params => `${v1Base}/punchlist/project/${params?.projectId}/punchlist/${params?.punchlistId}/participants`,
      },
      remove: {
        method: 'delete',
        url: params => `${v1Base}/punchlist/project/${params?.projectId}/punchlist/${params?.punchlistId}/participants/${params?.accountId}`,
      },
    },
    contractorAccounts: {
      list: {
        method: 'get',
        url: params => `${v1Base}/punchlist/project/${params?.projectId}/punchlist/${params?.punchlistId}/contractor-accounts`,
      },
      add: {
        method: 'post',
        url: params => `${v1Base}/punchlist/project/${params?.projectId}/punchlist/${params?.punchlistId}/contractor-accounts`,
      },
      remove: {
        method: 'delete',
        url: params => `${v1Base}/punchlist/project/${params?.projectId}/punchlist/${params?.punchlistId}/contractor-accounts/${params?.accountId}`,
      },
    },
    items: {
      list: {
        method: 'get',
        url: params => {
          const qp = new URLSearchParams();
          qp.set('start', String(params?.start ?? 0));
          qp.set('total', String(params?.total ?? 50));
          if (params?.search) qp.set('search', params.search);
          if (params?.status) qp.set('status', params.status);
          if (params?.severity) qp.set('severity', params.severity);
          if (params?.category) qp.set('category', params.category);
          if (params?.type) qp.set('type', params.type);
          if (params?.sort_by) qp.set('sort_by', params.sort_by);
          if (params?.sort_order) qp.set('sort_order', params.sort_order);
          return `${v1Base}/punchlist/project/${params?.projectId}/punchlist/${params?.punchlistId}/items?${qp.toString()}`;
        },
      },
      create: {
        method: 'post',
        url: params => `${v1Base}/punchlist/project/${params?.projectId}/punchlist/${params?.punchlistId}/items`,
      },
      get: {
        method: 'get',
        url: params => `${v1Base}/punchlist/items/${params?.itemId}`,
      },
      update: {
        method: 'patch',
        url: params => `${v1Base}/punchlist/items/${params?.itemId}`,
      },
      delete: {
        method: 'delete',
        url: params => `${v1Base}/punchlist/items/${params?.itemId}`,
      },
      submit: { method: 'post', url: p => `${v1Base}/punchlist/items/${p?.itemId}/submit` },
      approve: { method: 'post', url: p => `${v1Base}/punchlist/items/${p?.itemId}/approve` },
      reject: { method: 'post', url: p => `${v1Base}/punchlist/items/${p?.itemId}/reject` },
      'need-info': { method: 'post', url: p => `${v1Base}/punchlist/items/${p?.itemId}/need-info` },
      respond: { method: 'post', url: p => `${v1Base}/punchlist/items/${p?.itemId}/respond` },
      dispute: { method: 'post', url: p => `${v1Base}/punchlist/items/${p?.itemId}/dispute` },
      complete: { method: 'post', url: p => `${v1Base}/punchlist/items/${p?.itemId}/complete` },
      forwardToSub: { method: 'post', url: p => `${v1Base}/punchlist/items/${p?.itemId}/forward-to-sub` },
      forwardCandidates: { method: 'get', url: p => `${v1Base}/punchlist/items/${p?.itemId}/forward-candidates` },
      verify: { method: 'post', url: p => `${v1Base}/punchlist/items/${p?.itemId}/verify` },
      close: { method: 'post', url: p => `${v1Base}/punchlist/items/${p?.itemId}/close` },
      'send-back': { method: 'post', url: p => `${v1Base}/punchlist/items/${p?.itemId}/send-back` },
      reopen: { method: 'post', url: p => `${v1Base}/punchlist/items/${p?.itemId}/reopen` },
      issues: {
        list: { method: 'get', url: p => `${v1Base}/punchlist/items/${p?.itemId}/issues` },
        add: { method: 'post', url: p => `${v1Base}/punchlist/items/${p?.itemId}/issues` },
        remove: { method: 'delete', url: p => `${v1Base}/punchlist/items/${p?.itemId}/issues/${p?.issueId}` },
      },
      subAccounts: {
        list: { method: 'get', url: p => `${v1Base}/punchlist/items/${p?.itemId}/sub-accounts` },
        remove: { method: 'delete', url: p => `${v1Base}/punchlist/items/${p?.itemId}/sub-accounts/${p?.accountId}` },
      },
    },
    projectIssues: { method: 'get', url: p => `${v1Base}/punchlist/project/${p?.projectId}/issues` },
    nearbyIssues: {
      method: 'get',
      url: p => {
        const qp = new URLSearchParams();
        qp.set('lat', String(p?.lat));
        qp.set('lng', String(p?.lng));
        if (p?.radius) qp.set('radius', String(p.radius));
        if (p?.type) qp.set('type', p.type);
        if (p?.item_id) qp.set('item_id', String(p.item_id));
        if (p?.site_id) qp.set('site_id', String(p.site_id));
        return `${v1Base}/punchlist/project/${p?.projectId}/nearby-issues?${qp.toString()}`;
      },
    },
  },
};

export const v2URL = {
  billingSettings: {
    fetch: {
      method: 'get',
      url: params => `${v2Base}/project/${params?.projectId}/billing-settings`,
    },
    update: {
      method: 'put',
      url: params => `${v2Base}/project/${params?.projectId}/billing-settings`,
    },
  },
  projectCalendar: {
    fetch: {
      method: 'get',
      url: params => `${v2Base}/project/${params?.projectId}/calendar`,
    },
    update: {
      method: 'put',
      url: params => `${v2Base}/project/${params?.projectId}/calendar`,
    },
    initialize: {
      method: 'post',
      url: params => `${v2Base}/project/${params?.projectId}/calendar/initialize`,
    },
  },
  payAppSummary: {
    fetch: {
      method: 'get',
      url: params => `${v2Base}/project/${params?.projectId}/pay-app/${params?.payAppId}/summary`,
    },
  },
  payAppTemplates: {
    // Organization templates
    fetchOrgTemplates: {
      method: 'get',
      url: params => `${v2Base}/organization/${params?.organizationId}/payapp-templates`,
    },
    fetchOrgTemplate: {
      method: 'get',
      url: params => `${v2Base}/organization/${params?.organizationId}/payapp-template/${params?.templateId}`,
    },
    createOrgTemplate: {
      method: 'post',
      url: params => `${v2Base}/organization/${params?.organizationId}/payapp-template`,
    },
    updateOrgTemplate: {
      method: 'put',
      url: params => `${v2Base}/organization/${params?.organizationId}/payapp-template/${params?.templateId}`,
    },
    deleteOrgTemplate: {
      method: 'delete',
      url: params => `${v2Base}/organization/${params?.organizationId}/payapp-template/${params?.templateId}`,
    },
    // Project templates (includes org templates)
    fetchProjectTemplates: {
      method: 'get',
      url: params => `${v2Base}/project/${params?.projectId}/payapp-templates`,
    },
    createProjectTemplate: {
      method: 'post',
      url: params => `${v2Base}/project/${params?.projectId}/payapp-template`,
    },
    updateProjectTemplate: {
      method: 'put',
      url: params => `${v2Base}/project/${params?.projectId}/payapp-template/${params?.templateId}`,
    },
    deleteProjectTemplate: {
      method: 'delete',
      url: params => `${v2Base}/project/${params?.projectId}/payapp-template/${params?.templateId}`,
    },
    // Preview
    preview: {
      method: 'get',
      url: params => `${v2Base}/payapp-template/${params?.templateId}/preview${params?.payAppId ? '?payAppId=' + params.payAppId : ''}`,
    },
  },
  siteWiseDailyReportSummary: {
    fetch: {
      method: 'get',
      url: params => `${v2Base}/project/${params?.projectId}/daily-report-summary`,
    },
  },
  projectArchive: {
    list: {
      method: 'get',
      url: params => `${v2Base}/project/${params?.projectId}/archive`,
    },
    create: {
      method: 'post',
      url: params => `${v2Base}/project/${params?.projectId}/archive`,
    },
    download: {
      method: 'get',
      url: params => `${v2Base}/project/${params?.projectId}/archive/${params?.archiveId}/download`,
    },
  },
  arcgisPush: {
    fetch: {
      method: 'get',
      url: params => `${v2Base}/project/${params?.projectId}/arcgis-push`,
    },
    save: {
      method: 'put',
      url: params => `${v2Base}/project/${params?.projectId}/arcgis-push`,
    },
    test: {
      method: 'post',
      url: params => `${v2Base}/project/${params?.projectId}/arcgis-push/test`,
    },
    retry: {
      method: 'post',
      url: params => `${v2Base}/project/${params?.projectId}/arcgis-push/retry`,
    },
    backfill: {
      method: 'post',
      url: params => `${v2Base}/project/${params?.projectId}/arcgis-push/backfill`,
    },
  },
  issueTypes: {
    fetch: {
      method: 'get',
      url: params => `${v2Base}/project/${params?.projectId}/issue-types`,
    },
  },
  video: {
    fetch: {
      method: 'get',
      url: params => `${v2Base}/video/${params?.id}`,
    },
    deleteVideoFromMuxDb: {
      method: 'delete',
      url: params => `${v2Base}/video/${params}`,
    },
    getId: {
      method: 'get',
      url: () => `${v2Base}/video-upload-url `,
    },
  },
  translation: {
    method: 'get',
    url: params => `${v2Base}/project/${params?.projectId}/i18n/default`,
  },
  report: {
    download: {
      method: 'get',
      url: params => `${v2Base}/report/${params?.reportId}/render?${serializeQueryParams(params?.qp)}`,
    },
  },
  lookup: {
    fetch: {
      method: 'get',
      url: params =>
        `${v2Base}/project/${params?.projectId}/lookup/${params?.type}?${serializeQueryParams(params?.qp || {})}`,
    },
    create: {
      method: 'post',
      url: params => `${v2Base}/project/${params?.projectId}/lookup/${params?.type}`,
    },
    delete: {
      method: 'delete',
      url: params => `${v2Base}/project/${params?.projectId}/lookup/${params?.type}/${params?.id}`,
    },
  },
  field: {
    delete: {
      method: 'delete',
      url: params => `${v2Base}/field/${params?.fieldId}`,
    },
    update: {
      method: 'put',
      url: params => `${v2Base}/field/${params?.fieldId}?context=fields`,
    },
  },
  deliveryTrackingFields: {
    fetch: {
      method: 'get',
      url: params =>
        `${v2Base}/project/${params?.projectId}/fields?context=delivery_tracking_fields`,
    },
    create: {
      method: 'post',
      url: params =>
        `${v2Base}/project/${params?.projectId}/field?context=delivery_tracking_fields`,
    },
    update: {
      method: 'put',
      url: params =>
        `${v2Base}/field/${params?.id}?context=delivery_tracking_fields`,
    },
    delete: {
      method: 'delete',
      url: params =>
        `${v2Base}/field/${params?.field?.id || params?.fieldId}`,
    },
  },
  projectMaterials: {
    fetch: {
      method: 'get',
      url: params =>
        `${v2Base}/project/${params?.projectId}/materials?${serializeQueryParams(params?.qp || {})}`,
    },
  },
  bidItem: {
    fields: {
      method: 'get',
      url: params => `${v2Base}/bid-item/${params?.bidItemId}/fields?${serializeQueryParams(params?.qp || {})}`,
    },
    createField: {
      method: 'post',
      url: params => `${v2Base}/bid-item/${params?.bidItemId}/field?context=fields`,
    },
  },
  bidItemMaterial: {
    add: {
      method: 'put',
      url: params => `${v2Base}/material/${params?.materialId}/bid-item/${params?.bidItemId}`,
    },
    remove: {
      method: 'delete',
      url: params => `${v2Base}/material/${params?.materialId}/bid-item/${params?.bidItemId}`,
    },
  },
  bidItemSiteQuantity: {
    update: {
      method: 'put',
      url: params =>
        `${v2Base}/bid-item/${params?.bidItemId}/site/${params?.siteId}/${params?.quantity}`,
    },
  },
};
